import { flatten } from 'lodash-es';
import MuiVirtualizedTable, { IHeaderClickProps } from 'mui-virtualized-table';
import React from 'react';

import {
  getFWPublisherIDs,
  getPublisher,
  IAnalytics,
  IPublisher,
  IUser,
} from '~/src/dash/api/console-api';
import { convertECurrencyTypeToSymbol, ECurrencyType } from '~/src/dash/api/exchange-rates';
import { isIASReport } from '~/src/dash/api/ias-wrapper-api/ias-wrapper-api';
import { warn } from '~/src/dash/logging/logging';
import { sortAnalyticsFunctions } from '~/src/dash/utils/analyticsSorter';
import {
  formatAdBreakPosition,
  formatBoolean,
  formatCurrency,
  formatData,
  formatDate,
  formatDuration,
  formatHour,
  formatHugeNumber,
  formatIsPartialAdBreak,
  formatLatency,
  formatMediaTypeID,
  formatMonth,
  formatRate,
  formatSpotxChannelID,
  formatSpotxEndpoint,
  formatSyntheticID,
  IFormatFunction,
} from '~/src/dash/utils/format';
import {
  getGroupColumns,
  getMetricColumns,
  IColumn,
  presetAggregateToColumn,
  transformData,
} from '~/src/dash/utils/report-fields';
import { isCreativeTagEventsEnabled } from '~/src/dash/utils/ui';

interface IHBTableProps {
  loading: boolean;
  data: IAnalytics[];
  groupBy: string[];
  metrics: string[];
  TableProps?: any;
  height: number;
  width: number;
  obfuscateSiteTitle: boolean;
  sites: Map<string, string>;
  publisherID: string;
  currencyDisplayed: ECurrencyType;
  exchangeRate: number;
  reportType: string;
  isPresetAggregateSelected: boolean;
  user: IUser;
}

const formatFunctions: IFormatFunction[] = [
  {
    regex: new RegExp('SpotxEndpoint', 'i'),
    formattingFunction: formatSpotxEndpoint,
    name: 'formatSpotxEndpoint',
  },
  {
    regex: new RegExp('SpotxParamChannelID', 'i'),
    formattingFunction: formatSpotxChannelID,
    name: 'formatSpotxChannelID',
  },
  {
    regex: new RegExp('MediaTypeID', 'i'),
    formattingFunction: formatMediaTypeID,
    name: 'formatMediaTypeID',
  },
  {
    regex: new RegExp('SyntheticID', 'i'),
    formattingFunction: formatSyntheticID,
    name: 'formatSyntheticID',
  },
  { regex: new RegExp('rate$|.rate.', 'i'), formattingFunction: formatRate, name: 'formatRate' },
  {
    regex: new RegExp('(revenue.*|cpm.*|cpm$|revenue$|^avg.*bid$|lift|TTDSpend)', 'i'),
    formattingFunction: formatCurrency,
    name: 'formatCurrency',
  },
  { regex: new RegExp('latency$', 'i'), formattingFunction: formatLatency, name: 'formatLatency' },
  {
    regex: new RegExp('.+duration', 'i'),
    formattingFunction: formatDuration,
    name: 'formatDuration',
  },
  // https://regex101.com/r/hVjFbh/2
  { regex: new RegExp('^hour.*', 'i'), formattingFunction: formatHour, name: 'formatHour' },
  // https://regex101.com/r/xZJ785/1
  {
    regex: new RegExp('^day.*|StartDate', 'i'),
    formattingFunction: formatDate,
    name: 'formatDate',
  },
  // https://regex101.com/r/iNJFfu/1
  {
    regex: new RegExp('^(date|month|year).*', 'i'),
    formattingFunction: formatMonth,
    name: 'formatMonth',
  },
  // https://regex101.com/r/YGObU9/1
  {
    regex: new RegExp(
      '^(?!hour|date|day|month|year|AppBundleID|ContentID|DealID|BidDealId|InboundWseat|InboundDealId|OutboundDealId|SiteID|SpotxParamChannelID|BidderID|BidderName|SegmentName|Zip|StartDate|APKBuild|SamsungModel|DestinationSiteID|InventoryRouteID|DestinationID|PublisherID|PlacementID|MediaTypeID|PublicaChannelID|PublicaBidderID|LineItemID|AdvertiserID|OrderID|CreativeID|AdUnitID|AdSize|FwSectionIdV2|VenueID|NetworkID|NetworkChannelID|MediaFile).*',
      'i',
    ),
    formattingFunction: formatHugeNumber,
    name: 'formatHugeNumber',
  },
  {
    regex: new RegExp('AdBreakPosition', 'i'),
    formattingFunction: formatAdBreakPosition,
    name: 'formatAdBreakPosition',
  },
  {
    regex: new RegExp('IsPartialAdBreak', 'i'),
    formattingFunction: formatIsPartialAdBreak,
    name: 'formatIsParialAdBreak',
  },
  {
    regex: new RegExp('IsFallbackResponse', 'i'),
    formattingFunction: formatBoolean,
    name: 'formatIsFallbackResponse',
  },
  {
    regex: new RegExp('HasGooglePalSDKGivnParam', 'i'),
    formattingFunction: formatBoolean,
    name: 'formatHasGooglePalSDKGivnParam',
  },
];

function sortByGroupBy(groupBy: string[], data: IAnalytics[]): IAnalytics[] {
  // If groupBy Slot # then order by slot count
  if (groupBy.includes('custom1')) {
    return data.sort((a: IAnalytics, b: IAnalytics) => +a.Custom1 - +b.Custom1);
  }

  return data;
}

// get the columns for preset aggregates set in the group bys
function getColumnsForPresetAggregate(groupBys: string[]): IColumn[] {
  const columns: IColumn[] = [];

  groupBys.forEach((groupBy: string) => {
    // for each selected group by
    const column = presetAggregateToColumn.get(groupBy);
    if (column) {
      columns.push(column);
    }
  });

  return columns;
}

// create the columns title for the fullscreen report
function createHeadersColumns(
  reportType: string,
  fixedColumns: IColumn[],
  publisher: IPublisher,
  fwPublisherIDs: number[],
  metrics: string[],
  groupBys: string[],
  isPresetAggregateSelected: boolean,
  user: IUser,
) {
  let metricColumns: IColumn[] = [];
  if (!isPresetAggregateSelected) {
    // if no preset aggregate is selected, create metricColumns
    metricColumns = getMetricColumns(
      reportType,
      publisher.ID,
      fwPublisherIDs,
      user,
      isCreativeTagEventsEnabled(publisher),
      metrics,
    );
  } else {
    // if a preset aggregate is selected, use custom preset aggregate columns instead of metrics
    metricColumns = getColumnsForPresetAggregate(groupBys);
  }
  return flatten([fixedColumns, metricColumns]);
}

function sortData(data: IAnalytics[], colName: string, asc: boolean): IAnalytics[] {
  const ascOrDesc = asc ? -1 : 1;

  if (colName in sortAnalyticsFunctions) {
    // @ts-ignore
    const sortFunc = sortAnalyticsFunctions[colName];

    return data.sort((a: IAnalytics, b: IAnalytics) => {
      // @ts-ignore
      return sortFunc(a[colName], b[colName]) * ascOrDesc;
    });
  } else {
    warn('Column not in sortAnalytics: ', colName);
  }
  return data;
}

interface IState {
  fwPublisherIDs: number[];
  data: IAnalytics[];
  columns: any;
  fixedColumns: any;
  orderDirections: Map<string, 'asc' | 'desc'>;
  orderBy: string[];
}

export class ReportTable extends React.Component<IHBTableProps, IState> {
  state = {
    fwPublisherIDs: [],
    data: [],
    columns: [],
    fixedColumns: [],
    orderDirections: new Map<string, 'asc' | 'desc'>(),
    orderBy: [],
  };

  componentDidMount(): void {
    this.onClickSort = this.onClickSort.bind(this);
    const fixedColumns = getGroupColumns(
      this.props.publisherID,
      this.props.reportType,
      this.props.groupBy,
    );
    for (let i = 0; i < fixedColumns.length; i++) {
      const column = fixedColumns[i];
      if (['Month', 'Day', 'Hour'].includes(column.name)) {
        fixedColumns.splice(i, 1);
        fixedColumns.unshift(column);
        break;
      }
    }
    let mappedData = !isIASReport(this.props.reportType)
      ? (this.props.data.map(transformData) as IAnalytics[])
      : this.props.data;
    mappedData = sortByGroupBy(this.props.groupBy, mappedData);
    Promise.all([
      getFWPublisherIDs(),
      getPublisher({
        query: {
          publisher_id: this.props.publisherID,
        },
      }),
    ])
      .then((resp: any) => {
        const [fwPublisherIDs, publisher] = resp;
        const columns = createHeadersColumns(
          this.props.reportType,
          fixedColumns,
          publisher,
          fwPublisherIDs,
          this.props.metrics,
          this.props.groupBy,
          this.props.isPresetAggregateSelected,
          this.props.user,
        );
        const orderDirections = new Map<string, 'asc' | 'desc'>();

        columns.forEach((value: Partial<{ name: string }>) => {
          // @ts-ignore
          orderDirections[value.name] = 'asc';
        });

        this.setState({
          data: mappedData,
          fixedColumns,
          columns,
          orderDirections,
        });
      })
      .catch((err) => {
        console.error(err);
      });
  }

  onClickSort(_: any, props: IHeaderClickProps<any>) {
    // @ts-ignore
    const currentOrder = this.state.orderDirections[props.column.name];

    const nData = sortData(this.state.data, props.column.name, currentOrder === 'asc');

    // @ts-ignore
    this.state.orderDirections[props.column.name] = currentOrder === 'asc' ? 'desc' : 'asc';

    this.setState({
      data: nData,
      orderDirections: this.state.orderDirections,
    });
  }

  render() {
    const { height, reportType, width, TableProps, currencyDisplayed, exchangeRate } = this.props;
    const { columns, data, fixedColumns, orderBy, orderDirections } = this.state;
    const currencySymbol = convertECurrencyTypeToSymbol(currencyDisplayed);
    return (
      <MuiVirtualizedTable
        data={formatData(data, formatFunctions, reportType, exchangeRate, currencySymbol)}
        columns={columns}
        onHeaderClick={this.onClickSort}
        orderBy={orderBy}
        orderDirection={orderDirections}
        height={height}
        width={width}
        fixedRowCount={1}
        fixedColumnCount={fixedColumns.length}
        includeHeaders={true}
        {...TableProps}
      />
    );
  }
}
